// src/constants/irishHolidays.ts
export interface IrishHoliday {
  id: string;
  name: string;
  date: Date;
  type: 'fixed' | 'calculated';
}

// Fixed date holidays (same date every year)
const FIXED_HOLIDAYS = [
  { id: 'new-years-day', name: "New Year's Day", month: 0, day: 1 },
  { id: 'st-patricks-day', name: "St. Patrick's Day", month: 2, day: 17 },
  { id: 'christmas-day', name: 'Christmas Day', month: 11, day: 25 },
  { id: 'st-stephens-day', name: "St. Stephen's Day", month: 11, day: 26 }
];

export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
};

// Calculate Easter date (for Easter Monday)
const calculateEaster = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = Math.floor((h + l - 7 * m + 114) / 31);
  const p = (h + l - 7 * m + 114) % 31;
  
  return new Date(year, n - 1, p + 1);
};

// Get first Monday of a month
const getFirstMonday = (year: number, month: number): Date => {
  const firstDay = new Date(year, month, 1);
  const dayOfWeek = firstDay.getDay();
  const daysToAdd = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7;
  return new Date(year, month, 1 + daysToAdd);
};

// Get last Monday of a month
const getLastMonday = (year: number, month: number): Date => {
  const lastDay = new Date(year, month + 1, 0);
  const dayOfWeek = lastDay.getDay();
  const daysToSubtract = dayOfWeek === 1 ? 0 : (dayOfWeek + 6) % 7;
  return new Date(year, month, lastDay.getDate() - daysToSubtract);
};

// Get all Irish bank holidays for a specific year
export const getIrishHolidaysForYear = (year: number): IrishHoliday[] => {
  const holidays: IrishHoliday[] = [];

  // Add fixed holidays
  FIXED_HOLIDAYS.forEach(holiday => {
    holidays.push({
      id: holiday.id,
      name: holiday.name,
      date: new Date(year, holiday.month, holiday.day),
      type: 'fixed'
    });
  });

  // Add St. Brigid's Day (first Monday in February)
  holidays.push({
    id: 'st-brigids-day',
    name: "St. Brigid's Day",
    date: getFirstMonday(year, 1), // February
    type: 'calculated'
  });

  // Add Easter Monday
  const easter = calculateEaster(year);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);
  holidays.push({
    id: 'easter-monday',
    name: 'Easter Monday',
    date: easterMonday,
    type: 'calculated'
  });

  // Add May Day (first Monday in May)
  holidays.push({
    id: 'may-day',
    name: 'May Day',
    date: getFirstMonday(year, 4), // May
    type: 'calculated'
  });

  // Add June Holiday (first Monday in June)
  holidays.push({
    id: 'june-holiday',
    name: 'June Holiday',
    date: getFirstMonday(year, 5), // June
    type: 'calculated'
  });

  // Add August Holiday (first Monday in August)
  holidays.push({
    id: 'august-holiday',
    name: 'August Holiday',
    date: getFirstMonday(year, 7), // August
    type: 'calculated'
  });

  // Add October Holiday (last Monday in October)
  holidays.push({
    id: 'october-holiday',
    name: 'October Holiday',
    date: getLastMonday(year, 9), // October
    type: 'calculated'
  });

  // Sort by date
  return holidays.sort((a, b) => a.date.getTime() - b.date.getTime());
};

// Check if a specific date is an Irish bank holiday
export const isIrishHoliday = (date: Date): boolean => {
  const year = date.getFullYear();
  const holidays = getIrishHolidaysForYear(year);
  
  return holidays.some(holiday => {
    return holiday.date.getDate() === date.getDate() &&
           holiday.date.getMonth() === date.getMonth() &&
           holiday.date.getFullYear() === date.getFullYear();
  });
};

// Get holiday info for a specific date
export const getHolidayInfo = (date: Date): IrishHoliday | null => {
  const year = date.getFullYear();
  const holidays = getIrishHolidaysForYear(year);
  
  return holidays.find(holiday => {
    return holiday.date.getDate() === date.getDate() &&
           holiday.date.getMonth() === date.getMonth() &&
           holiday.date.getFullYear() === date.getFullYear();
  }) || null;
};

// Get holidays for a date range (useful for calendar views)
export const getHolidaysInRange = (startDate: Date, endDate: Date): IrishHoliday[] => {
  const years = new Set<number>();
  
  // Get all years in the range
  for (let d = new Date(startDate); d <= endDate; d.setFullYear(d.getFullYear() + 1)) {
    years.add(d.getFullYear());
  }
  
  const allHolidays: IrishHoliday[] = [];
  
  // Get holidays for each year and filter by date range
  years.forEach(year => {
    const yearHolidays = getIrishHolidaysForYear(year);
    yearHolidays.forEach(holiday => {
      if (holiday.date >= startDate && holiday.date <= endDate) {
        allHolidays.push(holiday);
      }
    });
  });
  
  return allHolidays.sort((a, b) => a.date.getTime() - b.date.getTime());
};